import { Button, LinkStyled } from '@components/common';
import { useTranslation } from 'next-i18next';
import { useJobTalentActions } from '@hooks/features/useJobTalentActions';
import { CheckIcon } from '@heroicons/react/24/solid';
import { ArchiveBoxIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { JobListType, JobPlacement } from '@type/job-params';
import { InterviewResponseButton } from '../after-apply/interview-selection/InterviewResponseButton';

interface CardJobTalentActionsProps {
  job: ApiSchema<'JobItem'>,
  requestId?: string,
  position?: number,
  listType?: JobListType,
}

export const CardJobTalentActions = (props: CardJobTalentActionsProps) => {
  // Destructure props
  const {
    job,
    requestId,
    position,
    listType,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Job Talent Actions Hook
  // *****************************************
  const {
    isApplying,
    applyToJob,
    hasApplied,
  } = useJobTalentActions({
    jobSlug: job.slug ?? '',
    jobId: job.id,
    jobTitle: job.title,
    company: job.companyName,
    likedProp: job.liked,
    dislikedProp: job.disliked,
    appliedProp: !!job.appliedAt,
    requestId,
    position,
    placement: JobPlacement.CARD,
    listType,
    withEasyApply: true,
    hasOwnApplyUrl: job.hasOwnApplyUrl,
    allowAfterApply: !job?.workplaceJob,
  });

  const needToCompleteVideoInterview = job.appliedAt && job?.unansweredJobInterviewQuestions && job?.unansweredJobInterviewQuestions?.length > 0;

  // Render component for job card actions
  // *****************************************
  const renderJobAction = () => {
    if (hasApplied) {
      return (
        <>
          {needToCompleteVideoInterview ? (
            <InterviewResponseButton
              jobSlug={job.slug!}
              className="flex flex-1 !text-xs"
            />
          ) : (
            <LinkStyled
              href="#"
              color="primary-light"
              rounding="full"
              styling="soft"
              size="xs"
              className="pointer-events-none flex flex-1 items-center justify-center overflow-hidden !text-xs"
            >
              <InformationCircleIcon className="-ml-1 mr-2 size-5 shrink-0" />
              <span className="text-center">{t('job.applied')}</span>
            </LinkStyled>
          )}
        </>
      );
    }
    if (job?.state === 'archived') {
      return (
        <>
          {/* Job archived message */}
          <LinkStyled
            href="#"
            color="primary-light"
            rounding="full"
            styling="soft"
            size="xs"
            className="flex flex-1 items-center justify-center overflow-hidden !text-xs"
          >
            <ArchiveBoxIcon className="-ml-1 mr-2 size-5 shrink-0" />
            <span className="text-center">{t('global.bnt.archived.job')}</span>
          </LinkStyled>
        </>
      );
    }

    return (
      <>

        {/* Fast apply button */}
        <Button
          styling="soft"
          color="secondary-light"
          rounding="full"
          size="xs"
          className="flex flex-1 items-center justify-center overflow-hidden !text-xs"
          isLoading={isApplying}
          spinnerClass="w-5 h-5 -ml-1 mr-2"
          onClick={applyToJob}
        >
          {!isApplying && <CheckIcon className="-ml-1 mr-2 size-5 shrink-0" />}
          <span className="text-center">
            {(() => {
              if (job?.state === 'active') {
                return t('job.tooltip.apply');
              }
              if (job?.state === 'passive') {
                return t('global.bnt.fast.apply.workplace.job');
              }
              return null;
            })()}
          </span>
        </Button>
      </>
    );
  };

  return (
    <div className="mb-3 flex items-stretch justify-center space-x-1.5">
      {renderJobAction()}
    </div>
  );
};
